import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Paper,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Container,
  Breadcrumbs,
  Link,
  Avatar,
  Pagination as MuiPagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Check as SaveIcon,
  Image as ImageIcon,
  CleaningServices as CleaningServicesIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Assessment as ValorizationIcon,
  Dashboard as DashboardIcon,
  Business as OperationsIcon,
} from '@mui/icons-material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Autocomplete from '@mui/material/Autocomplete';
import { useArticlesCore } from '../../../../core/modules/articles';
import { useCompanyCore } from '../../../../core/modules/company';
import { useValorizationCore } from '../../../../core/modules/valorization';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useContainerCore } from '../../../../core/modules/container';
import { useLocationCore } from '../../../../core/modules/location';
import { Article } from '../../../../core/interfaces/article.interface';
import { toast } from 'react-toastify';
import JSZip from 'jszip';

const Valorization: React.FC = () => {
  const { goToWelcome } = useNavigationCore();
  const { companyId } = useCompanyCore();
  const { getArticle, getArticles, getClassifiedArticles, classifiedArticles, articles, errorCode: articleErrorCode } = useArticlesCore();
  const { containers, getContainers } = useContainerCore();
  const { locations, getLocations } = useLocationCore();
  const { 
    valorization, 
    loading: valorizationLoading, 
    errorCode: valorizationErrorCode,
    getValorization,
    postValorization,
    clearValorization
  } = useValorizationCore();
  
  useEffect(() => {
    if (companyId) {
      getContainers(companyId, {});
      getLocations(companyId);
    }
  }, [companyId, getContainers, getLocations]);
  
  const [searchType, setSearchType] = useState<'barcode' | 'description' | 'container' | 'location'>('barcode');
  const [searchValue, setSearchValue] = useState('');
  const [selectedContainerId, setSelectedContainerId] = useState<number | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [searchPrice, setSearchPrice] = useState(false);
  const [marketPrice, setMarketPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [articleResults, setArticleResults] = useState<Article[]>([]);
  const [showResults, setShowResults] = useState(false);

  const handleSearchArticle = useCallback(async () => {
    if (!companyId) {
      toast.error('No hay compañía seleccionada');
      return;
    }

    if (searchType === 'container') {
      if (!selectedContainerId) {
        toast.error('Por favor seleccione un contenedor');
        return;
      }
      try {
        await getClassifiedArticles(companyId, { container_id: selectedContainerId }, () => {
          setShowResults(true);
        }, () => {
          setArticleResults([]);
          setShowResults(false);
          toast.error('No se encontraron artículos en este contenedor');
        });
      } catch (error: any) {
        toast.error('Error al buscar artículos por contenedor');
        setArticleResults([]);
        setShowResults(false);
      }
      return;
    }

    if (searchType === 'location') {
      if (!selectedLocationId) {
        toast.error('Por favor seleccione una ubicación');
        return;
      }
      try {
        await getClassifiedArticles(companyId, { location_id: selectedLocationId }, () => {
          setShowResults(true);
        }, () => {
          setArticleResults([]);
          setShowResults(false);
          toast.error('No se encontraron artículos en esta ubicación');
        });
      } catch (error: any) {
        toast.error('Error al buscar artículos por ubicación');
        setArticleResults([]);
        setShowResults(false);
      }
      return;
    }

    if (!searchValue.trim()) {
      toast.error('Por favor ingrese un valor de búsqueda');
      return;
    }

    if (searchType === 'description') {
      try {
        await getArticles(companyId, { description: searchValue }, () => {
          // onSuccess handled below
        }, () => {
          setArticleResults([]);
          setShowResults(false);
        });
        setShowResults(true);
      } catch (error: any) {
        const backendMsg = error?.response?.data?.message || 'Error al buscar artículos';
        toast.error(backendMsg);
        setArticleResults([]);
        setShowResults(false);
      }
      return;
    }

    // Código de barra (lógica original)
    try {
      await getArticle(companyId, { code: searchValue }, async (foundArticle: Article) => {
        setSelectedArticle(foundArticle);
        setShowResults(false);
        // Load existing valorizacion if available
        if (foundArticle) {
          try {
            await getValorization(foundArticle.id);
          } catch (err: any) {
            // Si es 404, limpiar el campo y no mostrar error
            if (err?.response?.status === 404) {
              setMarketPrice('');
              setNotes('');
            } else {
              toast.error('Error al buscar valorización previa');
            }
          }
        }
        toast.success('Artículo encontrado');
      }, () => {
        toast.error('No se encontró el artículo');
        setSelectedArticle(null);
        clearValorization();
      });
    } catch (error: any) {
      console.error('Error al buscar artículo:', error);
      toast.error('Error al buscar el artículo');
      setSelectedArticle(null);
      clearValorization();
    }
  }, [searchValue, companyId, getArticle, getArticles, getValorization, clearValorization, searchType, articles, selectedContainerId, selectedLocationId]);

  const handleBuscarPrecio = async () => {
    const description = selectedArticle?.name || selectedArticle?.description || searchValue;
    if (!description) {
      toast.error('No hay descripción disponible para buscar precio');
      return;
    }
    
    try {
      setSearchPrice(true);
      // Abrir Google para búsqueda manual
      const query = encodeURIComponent(description);
      window.open(`https://www.google.com/search?q=${query}+precio+chile`, '_blank');
      
      // TODO: Implementar búsqueda automática con API de búsqueda web y cálculo de precio promedio
      // Esto requeriría una API externa o servicio de scraping
      // Por ahora, el usuario debe buscar manualmente y copiar el precio
      
      toast.info('Busque el precio en Google y cópielo manualmente. En el futuro se implementará búsqueda automática con IA.');
    } catch (error) {
      console.error('Error al buscar precio:', error);
      toast.error('Error al buscar precio');
    } finally {
      setSearchPrice(false);
    }
  };

  const handleDescargarImagenes = async () => {
    if (!selectedArticle || !selectedArticle.files || selectedArticle.files.length === 0) {
      toast.error('No hay imágenes disponibles para descargar');
      return;
    }

    try {
      // Crear un archivo ZIP con todas las imágenes
      const zip = new JSZip();
      
      // Agregar cada imagen al ZIP
      for (let i = 0; i < selectedArticle.files.length; i++) {
        const file = selectedArticle.files[i];
        const response = await fetch(file.URL);
        const blob = await response.blob();
        const fileName = `imagen_${i + 1}_${selectedArticle.name.replace(/[^a-zA-Z0-9]/g, '_')}.jpg`;
        zip.file(fileName, blob);
      }
      
      // Generar y descargar el ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `imagenes_${selectedArticle.name.replace(/[^a-zA-Z0-9]/g, '_')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Se descargaron ${selectedArticle.files.length} imágenes`);
    } catch (error) {
      console.error('Error al descargar imágenes:', error);
      toast.error('Error al descargar las imágenes');
    }
  };

  const handleLimpiar = () => {
    setSearchValue('');
    setMarketPrice('');
    setNotes('');
    setSelectedArticle(null);
    setSearchPrice(false);
    clearValorization();
  };

  const handleGuardar = async () => {
    if (!selectedArticle) {
      toast.error('No hay artículo seleccionado');
      return;
    }
    if (!marketPrice) {
      toast.error('Por favor ingrese el precio de mercado');
      return;
    }
    
    try {
      await postValorization(
        selectedArticle.id,
        {
          market_price: parseFloat(marketPrice),
          notes: notes || undefined,
        },
        (savedValorization: any) => {
          toast.success('Valorización guardada exitosamente');
          // Update the market price field with the saved value
          setMarketPrice(savedValorization.market_price.toString());
          setNotes(savedValorization.notes || '');
        },
        () => {
          toast.error('Error al guardar la valorización');
        }
      );
    } catch (error) {
      toast.error('Error al guardar la valorización');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearchArticle();
    }
  };

  // Load existing valorization data when available
  useEffect(() => {
    if (valorization && selectedArticle) {
      setMarketPrice(valorization.market_price.toString());
      setNotes(valorization.notes || '');
    }
  }, [valorization, selectedArticle]);

  // Renderizar resultados de búsqueda por descripción
  const renderArticleResults = () => (
    showResults && articleResults.length > 0 && (
      <Box mt={2}>
        <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon color="primary" />
          Seleccione un artículo:
        </Typography>
        <Paper variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Imagen</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {articleResults.map((art) => (
                  <TableRow key={art.id} hover>
                    <TableCell>
                      <Avatar
                        src={art.files?.[0]?.URL}
                        alt={art.name}
                        sx={{ width: 40, height: 40 }}
                      >
                        {art.name?.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {art.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {art.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {art.code}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={() => {
                          setSelectedArticle(art);
                          setShowResults(false);
                          setSearchValue(art.code || '');
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        Seleccionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    )
  );

  useEffect(() => {
    if (showResults) {
      if (searchType === 'description') {
        setArticleResults(articles || []);
      } else if (searchType === 'container' || searchType === 'location') {
        // Para contenedor y ubicación, usar classifiedArticles
        setArticleResults((classifiedArticles as any) || []);
      }
    }
  }, [articles, classifiedArticles, showResults, searchType]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
     
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <ValorizationIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Valorización de Artículos
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Busca y selecciona artículos para realizar su valorización. Gestiona los precios y valores
          de los artículos de manera eficiente.
        </Typography>
      </Paper>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="primary" />
            Búsqueda de Artículo
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={searchType}
                  onChange={(e) => {
                    const newType = e.target.value as 'barcode' | 'description' | 'container' | 'location';
                    setSearchType(newType);
                    setSearchValue('');
                    setSelectedContainerId(null);
                    setSelectedLocationId(null);
                  }}
                >
                  <FormControlLabel 
                    value="barcode" 
                    control={<Radio />} 
                    label={<Typography>Código de Barra</Typography>}
                  />
                  <FormControlLabel 
                    value="description" 
                    control={<Radio />} 
                    label={<Typography>Descripción</Typography>}
                  />
                  <FormControlLabel 
                    value="container" 
                    control={<Radio />} 
                    label={<Typography>Contenedor</Typography>}
                  />
                  <FormControlLabel 
                    value="location" 
                    control={<Radio />} 
                    label={<Typography>Ubicación</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            
            {searchType === 'container' && (
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={containers || []}
                  getOptionLabel={(option: any) => `${option.code} - ${option.description || ''}`}
                  value={containers?.find((c: any) => c.id === selectedContainerId) || null}
                  onChange={(_: any, value: any) => setSelectedContainerId(value?.id || null)}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Contenedor"
                      placeholder="Seleccionar contenedor..."
                      variant="outlined"
                      size="small"
                      sx={{ 
                        backgroundColor: '#fff3cd',
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#ffc107',
                          },
                        },
                      }}
                    />
                  )}
                  noOptionsText="No hay contenedores disponibles"
                />
              </Grid>
            )}
            
            {searchType === 'location' && (
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={locations || []}
                  getOptionLabel={(option: any) => `${option.code} - ${option.description || ''}`}
                  value={locations?.find((l: any) => l.id === selectedLocationId) || null}
                  onChange={(_: any, value: any) => setSelectedLocationId(value?.id || null)}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Ubicación"
                      placeholder="Seleccionar ubicación..."
                      variant="outlined"
                      size="small"
                      sx={{ 
                        backgroundColor: '#fff3cd',
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#ffc107',
                          },
                        },
                      }}
                    />
                  )}
                  noOptionsText="No hay ubicaciones disponibles"
                />
              </Grid>
            )}
            
            {(searchType === 'barcode' || searchType === 'description') && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder={searchType === 'barcode' ? 'Ingrese código de barra' : 'Ingrese descripción'}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  sx={{ 
                    backgroundColor: '#fff3cd',
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#ffc107',
                      },
                    },
                  }}
                />
              </Grid>
            )}
            
            <Grid item>
              <Button
                variant="contained"
                onClick={handleSearchArticle}
                disabled={
                  (searchType === 'barcode' || searchType === 'description') && !searchValue.trim() ||
                  (searchType === 'container' && !selectedContainerId) ||
                  (searchType === 'location' && !selectedLocationId)
                }
                startIcon={<SearchIcon />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'medium',
                  minWidth: 150,
                }}
              >
                Buscar Artículo
              </Button>
            </Grid>
          </Grid>

          {articleErrorCode && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Error al buscar el artículo. Código de error: {articleErrorCode}
            </Alert>
          )}
          {renderArticleResults()}
        </CardContent>
      </Card>

      {/* Article Details Section */}
      {selectedArticle && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ValorizationIcon color="primary" />
              Detalles del Artículo
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ID"
                      value={selectedArticle.id}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Código"
                      value={selectedArticle.code || 'N/A'}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      value={selectedArticle.name}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Marca"
                      value={selectedArticle.brand || 'N/A'}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Descripción"
                      value={selectedArticle.description || 'N/A'}
                      InputProps={{ readOnly: true }}
                      size="small"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Precio"
                      value={selectedArticle.price ? `$${selectedArticle.price.toLocaleString()}` : 'N/A'}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Fecha del Precio"
                      value={selectedArticle.price_date ? new Date(selectedArticle.price_date).toLocaleDateString() : 'N/A'}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleBuscarPrecio}
                    startIcon={<SearchIcon />}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 'medium',
                    }}
                  >
                    Buscar Precio en Mercado
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={handleDescargarImagenes}
                    startIcon={<DownloadIcon />}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 'medium',
                    }}
                  >
                    Descargar Imágenes
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Market Price Section */}
      {selectedArticle && (
        <Card sx={{ mb: 3, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
              Valorización
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Precio de Mercado"
                  type="number"
                  value={marketPrice}
                  onChange={(e) => setMarketPrice(e.target.value)}
                  size="small"
                  sx={{ 
                    backgroundColor: '#d4edda',
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#28a745',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>$</Typography>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={searchPrice} 
                      onChange={(e) => setSearchPrice(e.target.checked)} 
                    />
                  }
                  label={<Typography>Incluir en búsqueda de precios</Typography>}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLimpiar}
                    startIcon={<CleaningServicesIcon />}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 'medium',
                    }}
                  >
                    Limpiar
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={handleGuardar}
                    disabled={!marketPrice || valorizationLoading}
                    startIcon={valorizationLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 'medium',
                    }}
                  >
                    {valorizationLoading ? 'Guardando...' : 'Guardar'}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notas de Valorización"
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ingrese notas adicionales sobre la valorización..."
                />
              </Grid>
            </Grid>

            {valorizationErrorCode && valorizationErrorCode !== 404 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error en la valorización. Código de error: {valorizationErrorCode}
              </Alert>
            )}

            {valorization && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Información de Valorización Guardada
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Precio Sugerido:</strong> ${valorization.suggested_price?.toLocaleString() || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Diferencia:</strong> ${valorization.price_difference?.toLocaleString() || 'N/A'} 
                      ({valorization.price_difference_percentage?.toFixed(2) || '0'}%)
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Última Actualización:</strong> {new Date(valorization.updated_at).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Article Images Section debajo de la información */}
      {selectedArticle && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <Card sx={{ boxShadow: 2, maxWidth: 1400, width: '100%', mx: 'auto' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                Imágenes del Artículo
              </Typography>
              {selectedArticle.files && selectedArticle.files.length > 0 ? (
                <ImageCarousel files={selectedArticle.files} />
              ) : (
                <Box 
                  sx={{ 
                    minHeight: 200, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    border: '2px dashed #ccc'
                  }}
                >
                  <Typography variant="body2">
                    No hay imágenes disponibles
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Empty State */}
      {!selectedArticle && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <SearchIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="text.secondary">
              Busque un artículo para comenzar la valorización
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ingrese un código de barra o descripción y haga clic en "Buscar Artículo"
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

const ImageCarousel = ({ files }: { files: { ID: number; URL: string }[] }) => {
  const [start, setStart] = React.useState(0);
  const visibleCount = 4;
  const total = files.length;

  // Scroll circular
  const handlePrev = () => setStart((prev) => (prev - visibleCount + total) % total);
  const handleNext = () => setStart((prev) => (prev + visibleCount) % total);

  if (total === 0) return null;

  // Calcular el rango de imágenes a mostrar (circular)
  const visibleFiles = [];
  for (let i = 0; i < Math.min(visibleCount, total); i++) {
    visibleFiles.push(files[(start + i) % total]);
  }

  return (
    <Box sx={{ width: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240, py: 1 }}>
      {/* Flecha izquierda */}
      <IconButton
        onClick={handlePrev}
        sx={{
          position: 'absolute',
          left: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          background: '#fff8',
          ':hover': { background: '#fff' },
          boxShadow: 2,
          width: 48,
          height: 48,
        }}
        size="large"
      >
        <Typography variant="h4">{'<'}</Typography>
      </IconButton>
      {/* Carrusel clásico de 4 imágenes */}
      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 3,
        mx: 'auto',
        px: { xs: 0, md: 2 },
      }}>
        {visibleFiles.map((file, idx) => (
          <Box key={file.ID} sx={{ flex: 1, maxWidth: '22%', minWidth: 0, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ width: '100%', aspectRatio: '1/1', maxWidth: 160, maxHeight: 160, minHeight: 100, background: '#f5f5f5', borderRadius: 8, boxShadow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <img
                src={file.URL}
                alt={`Imagen ${((start + idx) % total) + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
      {/* Flecha derecha */}
      <IconButton
        onClick={handleNext}
        sx={{
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          background: '#fff8',
          ':hover': { background: '#fff' },
          boxShadow: 2,
          width: 48,
          height: 48,
        }}
        size="large"
      >
        <Typography variant="h4">{'>'}</Typography>
      </IconButton>
    </Box>
  );
};

export default Valorization;